import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Home as HomeIcon, Calendar, ArrowRight } from "lucide-react";
import { Particles } from "@/components/Floating";
import Logo from "@/components/Logo";

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function NotFound() {
  useEffect(() => {
    document.title = "404 — Authority Detailing Co.";
  }, []);

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-[#050914] text-white grid place-items-center px-5 lg:px-8" data-testid="not-found-page">
      {/* Background atmosphere */}
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050914] via-transparent to-[#050914]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />
      <Particles count={16} />

      <div className="relative z-10 max-w-3xl mx-auto text-center py-20">
        <FadeIn>
          <Logo size={72} className="mx-auto mb-8 drop-shadow-2xl" />
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-4">
            Error · 404
          </div>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="font-display text-[120px] sm:text-[180px] lg:text-[220px] font-bold leading-[0.85] tracking-tighter">
            <span className="metallic-text">4</span>
            <span className="text-blue-500">0</span>
            <span className="metallic-text">4</span>
          </div>
        </FadeIn>

        <FadeIn delay={0.25}>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-6">
            This page took a detour.
          </h1>
        </FadeIn>

        <FadeIn delay={0.35}>
          <p className="mt-5 text-base sm:text-lg text-white/60 max-w-xl mx-auto leading-relaxed">
            Looks like the page you're chasing got buffed out. Don't worry — every great detail starts with the right turn. Let's get you back on track.
          </p>
        </FadeIn>

        <FadeIn delay={0.45}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#050914] font-semibold px-7 py-4 rounded-md transition-all shadow-lg"
              data-testid="not-found-home-btn"
            >
              <HomeIcon className="h-4 w-4" />
              Back to Home
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-4 rounded-md transition-all shadow-lg shadow-blue-600/40"
              data-testid="not-found-book-btn"
            >
              <Calendar className="h-4 w-4" />
              Book a Detail
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeIn>

        <FadeIn delay={0.55}>
          <div className="mt-12 flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-white/30">
            <div className="h-px w-12 bg-white/20" />
            Authority Detailing Co.
            <div className="h-px w-12 bg-white/20" />
          </div>
        </FadeIn>
      </div>
    </main>
  );
}
